import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertCircle, ArrowRight, Check, CheckCircle2, Loader2, Sparkles, Stethoscope,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function slugifyName(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get('canceled') === '1';

  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
    plan_code: 'free',
    primary_color: '#1d4ed8',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Plan list — public via SaaS plans endpoint
  useEffect(() => {
    axios.get(`${API}/api/saas/plans`).then(r => setPlans(r.data || []));
  }, []);

  // Auto-derive slug from clinic name unless the user typed one
  useEffect(() => {
    if (!slugTouched && form.name) {
      setForm(f => ({ ...f, slug: slugifyName(f.name) }));
    }
  }, [form.name, slugTouched]);

  // Slug availability check (debounced)
  useEffect(() => {
    if (!form.slug) { setSlugStatus({ checking: false, available: null, reason: null }); return; }
    const t = setTimeout(async () => {
      setSlugStatus(s => ({ ...s, checking: true }));
      try {
        const r = await axios.get(`${API}/api/tenant/slug-available`, { params: { slug: form.slug } });
        setSlugStatus({ checking: false, available: r.data.available, reason: r.data.reason || null });
      } catch (_) {
        setSlugStatus({ checking: false, available: null, reason: null });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.slug]);

  const selectedPlan = useMemo(
    () => plans.find(p => p.code === form.plan_code),
    [plans, form.plan_code]
  );
  const isPaid = (selectedPlan?.price_monthly || 0) > 0;

  const submit = async (startSubscription) => {
    setError('');
    if (!form.name || !form.slug || !form.admin_email || !form.admin_password) {
      setError('Please fill in clinic name, slug, admin email and password.');
      return;
    }
    if (slugStatus.available === false) {
      setError('That subdomain is taken — please choose another.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/tenant/signup`, {
        slug: form.slug,
        name: form.name,
        plan_code: form.plan_code,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
        admin_name: form.admin_name || 'Clinic Admin',
        primary_color: form.primary_color,
        start_subscription: startSubscription,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      // Free / trial — straight to a confirmation page.
      navigate(
        `/signup/success?slug=${encodeURIComponent(form.slug)}`,
        { state: { tenant: data.tenant, login_url: data.login_url, admin_email: form.admin_email } }
      );
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not create your workspace. Try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50" data-testid="signup-page">
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-blue-700" />
            <span className="font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              eCare 360
            </span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium text-zinc-600 hover:text-zinc-900"
            data-testid="signup-existing-tenant-link"
          >
            Already have a clinic? Sign in →
          </Link>
        </div>
      </header>

      <main className="flex-1 py-10 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left — pitch */}
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5" /> 14-day free trial on paid plans
            </div>
            <h1
              className="text-3xl font-bold text-zinc-900 mt-4 leading-tight"
              style={{ fontFamily: 'Manrope' }}
            >
              Spin up your own clinic workspace in 60 seconds.
            </h1>
            <p className="text-zinc-600 mt-3 text-[15px] leading-relaxed">
              Self-serve onboarding. Your own subdomain. Branded login. Invite
              your team and start charting patients today.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-zinc-700">
              {[
                'Patient & consultation management out of the box',
                'Pharmacy + purchase + stores modules included',
                'Voice dictation, e-Rx, lab workflows',
                'Your subdomain (acme-clinic.ecare360.app) — instantly',
                'Upgrade or downgrade anytime — cancel from your portal',
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-3">
            {canceled && (
              <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-sm">
                Your previous checkout was canceled. Your workspace is still saved; pick a plan to continue.
              </div>
            )}
            <Card className="shadow-xl border-0">
              <CardHeader>
                <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                  Create your workspace
                </CardTitle>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                    data-testid="signup-error">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
                  </div>
                )}
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Clinic / hospital name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Acme Clinic"
                      data-testid="signup-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Subdomain *</Label>
                    <div className="flex items-stretch border rounded-md overflow-hidden">
                      <Input
                        value={form.slug}
                        onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugifyName(e.target.value) }); }}
                        placeholder="acme-clinic"
                        className="border-0 rounded-none focus-visible:ring-0"
                        data-testid="signup-slug"
                      />
                      <div className="px-3 flex items-center bg-zinc-50 text-zinc-500 text-sm border-l">
                        .ecare360.app
                      </div>
                    </div>
                    {form.slug && (
                      <div className="text-[11px] mt-1 flex items-center gap-1.5" data-testid="signup-slug-status">
                        {slugStatus.checking ? (
                          <><Loader2 className="h-3 w-3 animate-spin" /> Checking…</>
                        ) : slugStatus.available === true ? (
                          <span className="text-emerald-700 flex items-center gap-1.5">
                            <CheckCircle2 className="h-3 w-3" /> {form.slug}.ecare360.app is available
                          </span>
                        ) : slugStatus.available === false ? (
                          <span className="text-red-700">
                            {slugStatus.reason === 'invalid' ? 'Invalid subdomain' : 'Already taken — try another'}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Your name</Label>
                      <Input
                        value={form.admin_name}
                        onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                        placeholder="Dr. Jane Doe"
                        data-testid="signup-admin-name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Primary color</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={form.primary_color}
                          onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                          className="h-10 w-14 border rounded-md cursor-pointer"
                          data-testid="signup-color"
                        />
                        <Input
                          value={form.primary_color}
                          onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                          className="font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Admin email *</Label>
                    <Input
                      type="email"
                      value={form.admin_email}
                      onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      placeholder="you@your-clinic.com"
                      data-testid="signup-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Choose a password *</Label>
                    <Input
                      type="password"
                      value={form.admin_password}
                      onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                      placeholder="Min 8 chars"
                      data-testid="signup-password"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label>Plan</Label>
                    <Select value={form.plan_code} onValueChange={(v) => setForm({ ...form, plan_code: v })}>
                      <SelectTrigger data-testid="signup-plan"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {plans.filter(p => p.is_active).map((p) => (
                          <SelectItem key={p.code} value={p.code}>
                            {p.name} — ${p.price_monthly}/mo
                            {p.price_monthly > 0 ? ' · 14-day free trial' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedPlan && (
                      <p className="text-[11px] text-zinc-500 mt-1">
                        {isPaid
                          ? `Starts a 14-day trial. You can begin a paid subscription now or later from your admin portal.`
                          : 'Free forever for small clinics — upgrade anytime.'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
                  <Button
                    onClick={() => submit(false)}
                    disabled={submitting}
                    className="flex-1 h-12 bg-blue-700 hover:bg-blue-800"
                    data-testid="signup-submit-trial"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {isPaid ? 'Start free trial' : 'Create my workspace'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  {isPaid && (
                    <Button
                      onClick={() => submit(true)}
                      disabled={submitting}
                      variant="outline"
                      className="flex-1 h-12"
                      data-testid="signup-submit-paid"
                    >
                      Pay &amp; subscribe now
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-zinc-500 mt-3 text-center">
                  By creating an account you agree to keep PHI compliant with your local regulations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
